/**
 * @sazeo/sdk — Attendance reporting extras (B19/B20)
 *
 * Extends the attendance addon with billable-hour summary hooks and a
 * client-side CSV export utility. The base `useAttendanceReport` and
 * `AttendanceReportRow` already live in addons.ts and are re-exported from
 * index.ts — this file adds what's missing:
 *
 *   - AttendanceBillableSummaryRow + useAttendanceBillableSummary
 *   - useExportAttendanceCsv (fetches CSV blob, triggers browser download)
 *   - exportAttendanceCsv (pure client-side CSV util for pre-loaded records)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export interface AttendanceEntry {
  id: string;
  tenant_id: string;
  user_id: string;
  entry_date: string;
  clock_in_at: string;
  clock_out_at: string | null;
  worked_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export interface AttendanceSummaryRow {
  user_id: string;
  total_minutes: number;
}

export interface AttendanceBillableSummaryRow {
  user_id: string;
  user_name: string | null;
  project_id: string | null;
  project_name: string | null;
  month_start: string; // ISO date "YYYY-MM-01"
  entry_count: number;
  total_hours: number;
  billable_hours: number;
}

export interface AttendanceBillableFilters {
  userId?: string;
  projectId?: string;
  billable?: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const attendanceKeys = {
  all: ['attendance-entries'] as const,
  list: (tenantId: string) => [...attendanceKeys.all, tenantId] as const,
  summary: (tenantId: string) => [...attendanceKeys.all, 'summary', tenantId] as const,
} as const;

export const attendanceBillableKeys = {
  all: ['attendance-billable'] as const,
  monthly: (tenantId: string, month: string) =>
    [...attendanceBillableKeys.all, tenantId, month] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns billable-hour summary from time_entries_monthly_summary view
 * via GET /addons/time-tracking/report. Accepts optional user/project filters.
 */
export function useAttendanceBillableSummary(
  supabase: SupabaseClientLike,
  tenantId: string,
  month?: string,
  filters: AttendanceBillableFilters = {},
) {
  const resolvedMonth = month ?? new Date().toISOString().slice(0, 7);
  const params = new URLSearchParams({ month: resolvedMonth });
  if (filters.userId) params.set('user_id', filters.userId);
  if (filters.projectId) params.set('project_id', filters.projectId);
  if (filters.billable !== undefined) params.set('billable', String(filters.billable));

  return useQuery({
    queryKey: attendanceBillableKeys.monthly(tenantId, resolvedMonth),
    queryFn: () =>
      apiFetch<AttendanceBillableSummaryRow[]>(
        supabase,
        `addons/time-tracking/report?${params.toString()}`,
      ),
    enabled: Boolean(tenantId),
  });
}

/**
 * Mutation that fetches the CSV blob from the Edge Function and triggers a
 * browser download. Input: `{ month: "YYYY-MM", filename?: string }`.
 */
export function useExportAttendanceCsv(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: async ({ month, filename }: { month: string; filename?: string }) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(
        `/functions/v1/addons/time-tracking/report/export?month=${month}&format=csv`,
        { headers },
      );
      if (!res.ok) throw new Error(`CSV export failed: ${res.statusText}`);
      const blob = await res.blob();
      triggerDownload(blob, filename ?? `time-report-${month}.csv`);
      return blob;
    },
  });
}

// ---------------------------------------------------------------------------
// Pure client-side CSV export utility
// ---------------------------------------------------------------------------

/**
 * Converts an array of flat objects to CSV and triggers a browser download.
 * Does not make any network requests.
 *
 * @param records   Array of flat objects; keys become the CSV header row.
 * @param filename  Download filename (e.g. "attendance-2026-04.csv").
 */
export function exportAttendanceCsv(
  records: Record<string, string | number | boolean | null | undefined>[],
  filename: string,
): void {
  if (records.length === 0) return;
  const first = records[0];
  if (!first) return;
  const headers = Object.keys(first);
  const csvRows = [
    headers.join(','),
    ...records.map((row) =>
      headers
        .map((h) => {
          const val = row[h];
          if (val === null || val === undefined) return '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(','),
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

// ---------------------------------------------------------------------------
// Attendance clock-in / clock-out hooks
// ---------------------------------------------------------------------------

/**
 * useAttendanceEntries — GET /addons/attendance/entries
 *
 * Returns the current user's attendance log (RLS-scoped to self).
 */
export function useAttendanceEntries(
  supabase: SupabaseClientLike,
  tenantId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: attendanceKeys.list(tenantId),
    queryFn: () => apiFetch<AttendanceEntry[]>(supabase, 'addons/attendance/entries'),
    enabled: Boolean(tenantId) && enabled,
  });
}

/**
 * useClockIn — POST /addons/attendance/clock-in
 *
 * Creates a new attendance_entries row for today. Server derives clock_in_at
 * from now(); no body is required.
 */
export function useClockIn(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<AttendanceEntry>(supabase, 'addons/attendance/clock-in', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

/**
 * useClockOut — POST /addons/attendance/clock-out
 *
 * Sets clock_out_at on today's open entry. No body required; server finds the
 * open row for the current user + today.
 */
export function useClockOut(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<AttendanceEntry>(supabase, 'addons/attendance/clock-out', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

/**
 * useAttendanceSummary — GET /addons/attendance/summary (admin/owner only)
 *
 * Returns total worked minutes per user for the current calendar month.
 */
export function useAttendanceSummary(
  supabase: SupabaseClientLike,
  tenantId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: attendanceKeys.summary(tenantId),
    queryFn: () => apiFetch<AttendanceSummaryRow[]>(supabase, 'addons/attendance/summary'),
    enabled: Boolean(tenantId) && enabled,
  });
}

// ---------------------------------------------------------------------------
// Shared DOM helper
// ---------------------------------------------------------------------------

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
